import { NoSsr } from "@mui/base";
import { useAtomValue, useSetAtom } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent, useCallback, useEffect } from "react";

import { BaseLink } from "shared/components/common/base-link";
import { config } from "shared/data";
import { accountAtom } from "shared/lib/account/atoms";

import { appBarScrollStickyAtom } from "scmp-app/components/app-bar/hooks/sticky/atoms";
import type { HeaderConfiguration } from "scmp-app/components/header/types";
import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { PlusBlurbPopupChecker } from "scmp-app/components/plus/blurb-popup";
import { usePlusBlurbPopup } from "scmp-app/components/plus/blurb-popup/hooks";
import { NavigationBar } from "scmp-app/components/plus/navigation-bar";
import { PlusPaywallSuspense } from "scmp-app/components/plus/plus-paywall-suspense";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { Destination } from "scmp-app/components/tracking/google-analytics-4/types";
import { appAtom } from "scmp-app/lib/app/atoms";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import { pathname as homepagePathname } from "scmp-app/pages/plus";
import { pathname as newsAgendaPathname } from "scmp-app/pages/plus/agenda";
import { pathname as archivePathname } from "scmp-app/pages/plus/archive";
import { pathname as chinaFutureTechPathname } from "scmp-app/pages/plus/cft";
import { pathname as searchPathName } from "scmp-app/pages/plus/search";

import { useGreetingMessage } from "./hooks";
import IconAgenda from "./icon-agenda.svg";
import IconArchive from "./icon-archive.svg";
import IconChinaFutureTech from "./icon-china-future-tech.svg";
import IconHome from "./icon-home.svg";
import IconPlusLogo from "./icon-plus-logo.svg";
import IconSearch from "./icon-search.svg";
import {
  AvatarContent,
  Container,
  Content,
  CtaMessage,
  CtaMessageWrapper,
  DividingLine,
  GreetingMessage,
  HeaderLink,
  IconBackground,
  IconLabel,
  LoginButtonWrapper,
  Placeholder,
  PlusHeaderContainer,
  StyledAvatar,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
};

type ActiveStatus = "" | "true";

type TopLevelPathname =
  | typeof archivePathname
  | typeof chinaFutureTechPathname
  | typeof homepagePathname
  | typeof newsAgendaPathname
  | typeof searchPathName;

export const PlusHeader: FunctionComponent<Props> = ({ className, headerConfiguration }) => {
  const { pathname } = useRouter();
  const { greetingMessage } = useGreetingMessage();
  const { openLoginDialog } = useLoginDialogStateHelper();

  const isActive = (linkPathname: TopLevelPathname): ActiveStatus => {
    if (pathname === linkPathname && homepagePathname === linkPathname) return "true";
    if (pathname.startsWith(linkPathname) && homepagePathname !== linkPathname) return "true";
    return "";
  };
  const setAppBarScrollSticky = useSetAtom(appBarScrollStickyAtom);

  useEffect(() => {
    setAppBarScrollSticky({ stickyScrollHeight: 0 });
  }, [setAppBarScrollSticky]);

  const handleLoginClick = useCallback(() => {
    openLoginDialog({
      application: "scmp-plus",
      destination: window.location.href,
      ga4CustomParameter: {
        trigger_point: "plus_homepage",
      },
    });
  }, [openLoginDialog]);

  const handleClickButtonGA4Tracking = () => {
    // GA4 21.1.1
    sendGA4Tracking({
      action: "click",
      category: "search",
      subcategory: "icon",
    });
  };

  const getRedirectClickButtonHandler = (destination: Destination) => () => {
    // GA4 18.1.1
    sendGA4Tracking({
      action: "click",
      category: "menu",
      customized_parameters: {
        action_type: "redirect",
        destination,
        menu_type: "oneline",
        product: "plus",
      },
      subcategory: "section",
    });
  };

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { isLoggedIn } = useAtomValue(accountAtom);
  const { checkPlusHomepagePathOnly } = usePlusBlurbPopup();
  const { navigatorInfo } = useAtomValue(appAtom);
  const isLightHouseCrawler = navigatorInfo?.isLightHouseCrawler;
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight;
  const hasScmpPlusUpgradeOptions = asyncRosettaState?.result?.user?.hasScmpPlusUpgradeOptions;
  const isShowPlusHeader =
    headerConfiguration?.appBarVariant !== "scmp/plus/learnmore" &&
    (checkPlusHomepagePathOnly || hasScmpPlusAccessRight || isLightHouseCrawler);

  const queryModule = "oneline_menu";

  const renderCtaMessage = useCallback(() => {
    const buttonDetail = (() => {
      if (!isLoggedIn) {
        return {
          ctaLabel: "Try for Free or Upgrade",
          redirectUrl: `${config.rosetta.subscribeHost}/plus#sign-up-section`,
        };
      }

      if (hasScmpPlusUpgradeOptions) {
        return {
          ctaLabel: "Upgrade",
          redirectUrl: `${config.account.scmpAccountHost}/manage/subscription?upgrade=true`,
        };
      }

      return {
        ctaLabel: "Try for Free",
        redirectUrl: `${config.rosetta.subscribeHost}/plus#sign-up-section`,
      };
    })();

    return (
      <CtaMessageWrapper>
        <BaseLink pathname={buttonDetail.redirectUrl}>
          <CtaMessage>{buttonDetail.ctaLabel}</CtaMessage>
        </BaseLink>
        {!isLoggedIn && (
          <>
            <CtaMessage>/</CtaMessage>
            <LoginButtonWrapper>
              <CtaMessage onClick={handleLoginClick}>Log in</CtaMessage>
            </LoginButtonWrapper>
          </>
        )}
      </CtaMessageWrapper>
    );
  }, [handleLoginClick, hasScmpPlusUpgradeOptions, isLoggedIn]);

  return (
    <NoSsr>
      <Container className={className}>
        <NavigationBar />
        {isShowPlusHeader && (
          <PlusHeaderContainer>
            <PlusPaywallSuspense fallback={<Placeholder />}>
              <Wrapper>
                <Content>
                  <PlusBlurbPopupChecker section="scmp-plus-highlights">
                    <HeaderLink
                      $active={isActive(searchPathName)}
                      onClick={handleClickButtonGA4Tracking}
                      pathname={searchPathName}
                    >
                      <IconBackground>
                        <IconSearch />
                      </IconBackground>
                    </HeaderLink>
                  </PlusBlurbPopupChecker>

                  <DividingLine />

                  <HeaderLink
                    $active={isActive(homepagePathname)}
                    onClick={getRedirectClickButtonHandler("homepage")}
                    pathname={homepagePathname}
                    query={{
                      module: queryModule,
                      pgtype: "homepage",
                    }}
                  >
                    <IconBackground>
                      <IconHome />
                    </IconBackground>
                    <IconLabel>Home</IconLabel>
                  </HeaderLink>

                  <PlusBlurbPopupChecker section="news-agenda">
                    <HeaderLink
                      $active={isActive(newsAgendaPathname)}
                      onClick={getRedirectClickButtonHandler("agenda")}
                      pathname={newsAgendaPathname}
                      query={{
                        module: queryModule,
                        pgtype: "agenda",
                      }}
                    >
                      <IconBackground>
                        <IconAgenda />
                      </IconBackground>
                      <IconLabel>News Agenda</IconLabel>
                    </HeaderLink>
                  </PlusBlurbPopupChecker>

                  <PlusBlurbPopupChecker section="scmp-plus-highlights">
                    <HeaderLink
                      $active={isActive(archivePathname)}
                      onClick={getRedirectClickButtonHandler("archive")}
                      pathname={archivePathname}
                      query={{
                        module: queryModule,
                        pgtype: "archive",
                      }}
                    >
                      <IconBackground>
                        <IconArchive />
                      </IconBackground>
                      <IconLabel>Plus Archive</IconLabel>
                    </HeaderLink>
                  </PlusBlurbPopupChecker>

                  <PlusBlurbPopupChecker section="scmp-plus-highlights">
                    <HeaderLink
                      $active={isActive(chinaFutureTechPathname)}
                      onClick={getRedirectClickButtonHandler("archive")}
                      pathname={chinaFutureTechPathname}
                      query={{
                        module: queryModule,
                        pgtype: "china_future_tech",
                      }}
                    >
                      <IconBackground>
                        <IconChinaFutureTech />
                      </IconBackground>
                      <IconLabel>China Future Tech</IconLabel>
                    </HeaderLink>
                  </PlusBlurbPopupChecker>
                </Content>
                <IconPlusLogo />
                <AvatarContent>
                  {hasScmpPlusAccessRight ? (
                    <GreetingMessage>{greetingMessage}</GreetingMessage>
                  ) : (
                    renderCtaMessage()
                  )}
                  {isLoggedIn ? (
                    <BaseLink pathname={`${config.account.scmpAccountHost}/settings`}>
                      <StyledAvatar id="plus-header" />
                    </BaseLink>
                  ) : (
                    <StyledAvatar id="plus-header" onClick={handleLoginClick} />
                  )}
                </AvatarContent>
              </Wrapper>
            </PlusPaywallSuspense>
          </PlusHeaderContainer>
        )}
      </Container>
    </NoSsr>
  );
};

PlusHeader.displayName = "PlusHeader";
